import { Input } from "@/components/ui/input";
import {
  formatThousands,
  unformatThousands,
  formatMoney,
} from "@/lib/detailSchemas";

const num = (v) => {
  const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const unitPriceStr = (alan, deger) => {
  const a = num(alan);
  const d = num(deger);
  if (a > 0 && d > 0) return (d / a).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
  return "—";
};

// Arsa Değeri: [alan] m² × [birim] ₺/m² = [toplam] ₺  (units outside inputs)
function ArsaRow({ alan, deger, onAlan, onDeger }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-white overflow-hidden">
      <div className="px-3 py-2 flex items-center gap-2 border-b border-zinc-100 bg-emerald-50">
        <span className="h-3 w-1 rounded-sm bg-emerald-500" />
        <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-800">
          Arsa Değeri
        </div>
      </div>
      <div className="p-3">
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1.4fr)] gap-2 items-center">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <Input
              type="text"
              inputMode="numeric"
              value={formatThousands(alan ?? "")}
              onChange={(e) => onAlan(unformatThousands(e.target.value))}
              placeholder="0"
              className="h-10 rounded-sm text-base font-bold text-zinc-900 text-right min-w-0"
              data-testid="arsa-alan-input"
            />
            <span className="text-xs font-semibold text-zinc-600 whitespace-nowrap">m²</span>
          </div>
          <span className="text-zinc-500 text-base font-bold">×</span>
          <div className="flex items-baseline gap-1.5 min-w-0">
            <div
              className="flex-1 h-10 rounded-sm border border-dashed border-zinc-300 bg-zinc-50 px-2.5 flex items-center justify-end text-base font-bold text-zinc-800"
              title="Otomatik hesaplanır"
              data-testid="arsa-unit-price"
            >
              {unitPriceStr(alan, deger)}
            </div>
            <span className="text-xs font-semibold text-zinc-600 whitespace-nowrap">₺/m²</span>
          </div>
          <span className="text-zinc-500 text-base font-bold">=</span>
          <div className="flex items-baseline gap-1.5 min-w-0">
            <Input
              type="text"
              inputMode="numeric"
              value={formatThousands(deger ?? "")}
              onChange={(e) => onDeger(unformatThousands(e.target.value))}
              placeholder="0"
              className="h-10 rounded-sm text-base font-extrabold text-zinc-900 text-right min-w-0 border-l-4 border-l-emerald-500"
              data-testid="arsa-degeri-input"
            />
            <span className="text-xs font-semibold text-zinc-600 whitespace-nowrap">₺</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function BinaTotalRow({ label, value, onChange, colorClasses, testId }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-white overflow-hidden">
      <div className={`px-3 py-2 flex items-center gap-2 border-b border-zinc-100 ${colorClasses.headerBg}`}>
        <span className={`h-3 w-1 rounded-sm ${colorClasses.accent}`} />
        <div className={`text-[11px] uppercase tracking-widest font-bold ${colorClasses.headerText}`}>
          {label}
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-baseline gap-1.5">
          <Input
            type="text"
            inputMode="numeric"
            value={formatThousands(value ?? "")}
            onChange={(e) => onChange(unformatThousands(e.target.value))}
            placeholder="0"
            className={`h-10 rounded-sm text-base font-extrabold text-zinc-900 text-right min-w-0 border-l-4 ${colorClasses.totalBorder}`}
            data-testid={testId}
          />
          <span className="text-xs font-semibold text-zinc-600 whitespace-nowrap">₺</span>
        </div>
      </div>
    </div>
  );
}

function TotalCard({ label, value, tone, testId }) {
  // tone: 'yasal' (emerald green) | 'mevcut' (rose pink)
  const cls =
    tone === "yasal"
      ? {
          bg: "bg-emerald-600",
          text: "text-white",
          border: "border-emerald-500",
          labelText: "text-emerald-100",
        }
      : {
          bg: "bg-rose-500",
          text: "text-white",
          border: "border-rose-400",
          labelText: "text-rose-100",
        };
  const numStr = value > 0 ? value.toLocaleString("tr-TR") : "0";
  return (
    <div className={`rounded-sm border ${cls.border} ${cls.bg} p-3`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${cls.labelText} mb-1.5`}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <div
          className={`text-2xl font-extrabold ${cls.text} tracking-tight leading-none`}
          data-testid={testId}
        >
          {numStr}
        </div>
        <span className={`text-sm font-bold ${cls.text}/90`}>₺</span>
      </div>
    </div>
  );
}

export default function ArsaBinaValueRows({ details, onChange }) {
  const setField = (key, value) => onChange({ ...details, [key]: value });

  const arsaDeger = num(details.arsa_degeri);
  const yasalBina = num(details.yasal_bina_toplam);
  const mevcutBina = num(details.mevcut_bina_toplam);
  const yasalToplam = arsaDeger + yasalBina;
  const mevcutToplam = arsaDeger + mevcutBina;

  return (
    <div className="space-y-2.5">
      <div className="rounded-sm border border-emerald-300 bg-emerald-50 p-2.5 flex items-start gap-2">
        <span className="text-emerald-700 mt-0.5 shrink-0">ℹ</span>
        <p className="text-[11px] text-emerald-900 leading-snug">
          <strong>Değer girişi ipucu:</strong> Bu taşınmaz türünde <span className="font-semibold">asıl değer Arsa Değeri</span> satırıdır.
          Yasal ve Mevcut <span className="font-semibold">Bina Değerleri Toplamı</span> satırları yalnızca
          <span className="font-semibold"> özet bilgi</span> amaçlıdır; girseniz de girmeseniz de arsa değerini etkilemez.
        </p>
      </div>

      <ArsaRow
        alan={details.yasal_alan}
        deger={details.arsa_degeri}
        onAlan={(v) => setField("yasal_alan", v)}
        onDeger={(v) => setField("arsa_degeri", v)}
      />

      <BinaTotalRow
        label="Yasal Bina Değerleri Toplamı"
        value={details.yasal_bina_toplam}
        onChange={(v) => setField("yasal_bina_toplam", v)}
        testId="yasal-bina-toplam-input"
        colorClasses={{
          headerBg: "bg-emerald-50",
          headerText: "text-emerald-800",
          accent: "bg-emerald-500",
          totalBorder: "border-l-emerald-500",
        }}
      />

      <BinaTotalRow
        label="Mevcut Bina Değerleri Toplamı"
        value={details.mevcut_bina_toplam}
        onChange={(v) => setField("mevcut_bina_toplam", v)}
        testId="mevcut-bina-toplam-input"
        colorClasses={{
          headerBg: "bg-rose-50",
          headerText: "text-rose-800",
          accent: "bg-rose-500",
          totalBorder: "border-l-rose-500",
        }}
      />

      {/* Auto-computed totals summary */}
      <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-3 w-1 rounded-sm bg-white" />
          <div className="text-[11px] uppercase tracking-widest font-bold text-white">
            Toplam Değer (Otomatik)
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <TotalCard
            label="Yasal Toplam"
            value={yasalToplam}
            tone="yasal"
            testId="yasal-toplam-auto"
          />
          <TotalCard
            label="Mevcut Toplam"
            value={mevcutToplam}
            tone="mevcut"
            testId="mevcut-toplam-auto"
          />
        </div>
      </div>
    </div>
  );
}
